// src/components/home/MonthlyAnalytics/MonthlyPivotTable.jsx
import React, { useState, useMemo } from 'react';
import styles from './MonthlyPivotTable.module.css';
import DailySalesModal from './DailySalesModal';

const fmt = (n) => (n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const MonthlyPivotTable = ({ data = [], type, categoryFilter }) => {
    const [search,       setSearch]       = useState('');
    const [page,         setPage]         = useState(1);
    const [showModal,    setShowModal]    = useState(false);
    const [selectedCell, setSelectedCell] = useState(null);
    const PAGE_SIZE = 10;

    const dimKey = useMemo(() => {
        switch (type) {
            case 'salesperson': return 'Sales Person Name';
            case 'state':       return 'State';
            case 'category':    return 'Category';
            default:            return 'Customer Name';
        }
    }, [type]);

    // All month columns sorted desc
    const months = useMemo(() => {
        if (!data.length) return [];
        const allKeys = new Set();
        data.forEach(row => Object.keys(row).forEach(k => allKeys.add(k)));
        const monthCols = Array.from(allKeys).filter(k => /^[A-Za-z]{3} \d{4}$/.test(k));
        return monthCols.sort((a, b) => {
            const parse = (s) => { const [m, y] = s.split(' '); return new Date(`${m} 1, ${y}`); };
            return parse(b) - parse(a);
        });
    }, [data]);

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        return data.filter(row =>
            Object.values(row).some(v => String(v).toLowerCase().includes(search.toLowerCase()))
        );
    }, [data, search]);

    const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
    const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleCellClick = (monthKey, row, sales) => {
        if (!sales || sales === 0) return;
        const [monthName, yearStr] = monthKey.split(' ');
        const monthNum = new Date(`${monthName} 1, ${yearStr}`).getMonth() + 1;
        const filterValue = row[dimKey] || '';

        setSelectedCell({
            month: monthNum,
            year: parseInt(yearStr),
            monthName,
            type,
            filterValue,
            categoryFilter,
            rowData: row,
        });
        setShowModal(true);
    };

    const renderCell = (row, monthKey) => {
        const sales = row[monthKey] || 0;
        const cogs  = row[monthKey + '_COGS'] || 0;
        const lines = row[monthKey + '_Lines'] || 0;
        const gm    = sales > 0 ? ((sales - cogs) * 100 / sales).toFixed(2) : 0;

        return (
            <td
                key={monthKey}
                className={`${styles.monthCell} ${sales > 0 ? styles.clickable : ''}`}
                onClick={() => handleCellClick(monthKey, row, sales)}
                title={sales > 0 ? 'Click for daily breakdown' : ''}
            >
                <div className={styles.salesRow}>₹{fmt(sales)}</div>
                <div className={styles.linesRow}>{fmt(lines)} lines</div>
                <div className={styles.gmRow}>{gm}% GM</div>
                <div className={styles.cogsRow}>₹{fmt(cogs)}</div>
            </td>
        );
    };

    const renderTotalCell = (row) => {
        const sales = row['Total Sales'] || 0;
        const cogs  = row['Total COGS']  || 0;
        const lines = row['Total Line Items'] || 0;
        const gm    = row['GM%'] || 0;

        return (
            <td className={styles.totalCell}>
                <div className={styles.salesRow}>₹{fmt(sales)}</div>
                <div className={styles.linesRow}>{fmt(lines)} lines</div>
                <div className={styles.gmRow}>{gm}% GM</div>
                <div className={styles.cogsRow}>₹{fmt(cogs)}</div>
            </td>
        );
    };

    if (!data.length) {
        return <div className={styles.empty}>No data available.</div>;
    }

    return (
        <div className={styles.wrapper}>
            {/* Search + export row */}
            <div className={styles.toolbar}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className={styles.searchInput}
                />
                <span className={styles.count}>{filtered.length} records</span>
            </div>

            <div className={styles.tableScroll}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th className={`${styles.th} ${styles.stickyCol}`}>{dimKey}</th>
                            <th className={`${styles.th} ${styles.totalTh}`}>Total</th>
                            {months.map(m => (
                                <th key={m} className={styles.th}>{m}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paged.map((row, idx) => (
                            <tr key={idx} className={styles.tr}>
                                <td className={`${styles.dimCell} ${styles.stickyCol}`}>
                                    {row[dimKey]}
                                </td>
                                {renderTotalCell(row)}
                                {months.map(m => renderCell(row, m))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className={styles.pagination}>
                <button onClick={() => setPage(1)}         disabled={page === 1}>First</button>
                <button onClick={() => setPage(p => p-1)}  disabled={page === 1}>Prev</button>
                <span>Page {page} of {pageCount || 1}</span>
                <button onClick={() => setPage(p => p+1)}  disabled={page >= pageCount}>Next</button>
                <button onClick={() => setPage(pageCount)} disabled={page >= pageCount}>Last</button>
            </div>

            {selectedCell && (
                <DailySalesModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    month={selectedCell.month}
                    year={selectedCell.year}
                    monthName={selectedCell.monthName}
                    type={selectedCell.type}
                    filterValue={selectedCell.filterValue}
                    categoryFilter={selectedCell.categoryFilter}
                    rowData={selectedCell.rowData}
                />
            )}
        </div>
    );
};

export default MonthlyPivotTable;